import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Stack, Text, TextInput, UnstyledButton } from '@cowbell/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faBolt,
  faBook,
  faBookOpen,
  faBell,
  faBellOn,
  faBrain,
  faBuilding,
  faCalendar,
  faCalendarCheck,
  faCalendarDays,
  faChartLine,
  faChartPie,
  faChartSimple,
  faChevronDown,
  faClipboard,
  faClipboardCheck,
  faClipboardList,
  faEnvelope,
  faEnvelopeOpen,
  faFile,
  faFileContract,
  faFileInvoice,
  faFileLines,
  faFileShield,
  faFlag,
  faFolder,
  faFolderOpen,
  faGear,
  faGears,
  faHandshake,
  faImage,
  faLightbulb,
  faListCheck,
  faMagnifyingGlass,
  faMagnifyingGlassChart,
  faRobot,
  faScaleBalanced,
  faShieldCheck,
  faShieldHalved,
  faTag,
  faUserShield,
  faUsers,
} from '@fortawesome/pro-regular-svg-icons';
import styles from './IconPickerField.module.css';

interface IconPickerFieldProps {
  readonly value: string;
  readonly onChange: (iconName: string) => void;
  readonly label: string;
}

interface IconOption {
  readonly name: string;
  readonly icon: IconDefinition;
  readonly label: string;
}

/**
 * `PascalCase-after-"fa"` -> spaced label, e.g. 'faBookOpen' -> 'Book Open'.
 * Derived rather than hand-typed per icon so the ~40-entry list below can't
 * drift out of sync with its own labels.
 */
function labelFromName(name: string): string {
  return name
    .slice(2)
    .replace(/([A-Z])/g, ' $1')
    .trim();
}

function option(name: string, icon: IconDefinition): IconOption {
  return { name, icon, label: labelFromName(name) };
}

/**
 * Curated ~40-icon subset (OMNI-831 Task 4), each imported explicitly (no
 * wildcard import of the icon package) per the task brief. Covers the
 * requested families — bolt, book, chart, shield, clipboard, envelope,
 * calendar, gears, magnifying-glass, scale, file, bell — plus a handful of
 * general-purpose glyphs (robot, brain, folder, users, building, handshake,
 * tag, flag, list-check) likely to fit a custom skill's subject matter.
 */
const ICON_OPTIONS: readonly IconOption[] = [
  option('faBolt', faBolt),
  option('faBook', faBook),
  option('faBookOpen', faBookOpen),
  option('faChartLine', faChartLine),
  option('faChartPie', faChartPie),
  option('faChartSimple', faChartSimple),
  option('faShieldHalved', faShieldHalved),
  option('faShieldCheck', faShieldCheck),
  option('faClipboard', faClipboard),
  option('faClipboardList', faClipboardList),
  option('faClipboardCheck', faClipboardCheck),
  option('faEnvelope', faEnvelope),
  option('faEnvelopeOpen', faEnvelopeOpen),
  option('faCalendar', faCalendar),
  option('faCalendarDays', faCalendarDays),
  option('faCalendarCheck', faCalendarCheck),
  option('faGear', faGear),
  option('faGears', faGears),
  option('faMagnifyingGlass', faMagnifyingGlass),
  option('faMagnifyingGlassChart', faMagnifyingGlassChart),
  option('faScaleBalanced', faScaleBalanced),
  option('faFile', faFile),
  option('faFileLines', faFileLines),
  option('faFileInvoice', faFileInvoice),
  option('faFileContract', faFileContract),
  option('faFileShield', faFileShield),
  option('faBell', faBell),
  option('faBellOn', faBellOn),
  option('faRobot', faRobot),
  option('faBrain', faBrain),
  option('faLightbulb', faLightbulb),
  option('faFolderOpen', faFolderOpen),
  option('faFolder', faFolder),
  option('faUserShield', faUserShield),
  option('faUsers', faUsers),
  option('faBuilding', faBuilding),
  option('faHandshake', faHandshake),
  option('faTag', faTag),
  option('faFlag', faFlag),
  option('faListCheck', faListCheck),
];

const ICON_BY_NAME = new Map(ICON_OPTIONS.map((opt) => [opt.name, opt]));

/** True when `name` is one of the icons this picker offers. */
export function isPickerIcon(name: string | undefined): boolean {
  return !!name && ICON_BY_NAME.has(name);
}

/**
 * Searchable grid popover over a curated FontAwesome icon subset, replacing
 * the free-text "Font Awesome icon name, e.g. faBolt" input on the custom
 * skill editor (OMNI-831 Task 4) — see task-4-report.md for why this keeps
 * storing an explicit `faName` string rather than a lookup key (no other
 * surface renders a custom skill's `icon` today, so there is no existing
 * lookup-key consumer to match).
 *
 * Positioned via a manual `createPortal` + measured rect, NOT `@cowbell/ui`'s
 * `Popover` — confirmed live that Popover doesn't "just work" here. The
 * custom skill editor renders inside `WizardShell`, whose form column
 * (`WizardShell.module.css` `.formColumn`) sets `overflow: hidden`. Every
 * Mantine Popover unconditionally includes floating-ui's `hide()` middleware
 * (`@mantine/core` `use-popover.mjs`), and `PopoverDropdown` forces
 * `display: none` whenever that middleware reports the reference clipped —
 * regardless of the controlled `opened` state. Live DOM check confirmed the
 * failure mode precisely: `aria-expanded` flips to `"true"` and the trigger's
 * `aria-controls` id is set correctly, but no dropdown node with that id ever
 * mounts (`.mantine-Popover-dropdown` count stayed at 7 before AND after the
 * click — nothing new appeared). This is the repo's known
 * "custom popups can render off-screen via Portal" failure class (see
 * AGENTS.md); its established fix is exactly this file's approach, modeled
 * directly on `AgentStudioPage.tsx`'s "Shared With" search dropdown
 * (measure the trigger's rect, `createPortal` into `#moo-studio-portal`).
 */
export default function IconPickerField({
  value,
  onChange,
  label,
}: IconPickerFieldProps) {
  const [opened, setOpened] = useState(false);
  const [query, setQuery] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Reset the search on every close path (outside click, Escape, re-clicking
  // the trigger, or picking an icon) so reopening always starts from the full
  // grid rather than silently keeping a stale filter with no visible reason
  // it's filtered at all.
  useEffect(() => {
    if (!opened) setQuery('');
  }, [opened]);

  // Close on outside click / Escape — Popover provides this for free;
  // reproduced manually since this bypasses Popover entirely.
  useEffect(() => {
    if (!opened) return undefined;

    function handlePointerDown(event: MouseEvent): void {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setOpened(false);
    }
    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') setOpened(false);
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [opened]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return ICON_OPTIONS;
    return ICON_OPTIONS.filter(
      (opt) =>
        opt.name.toLowerCase().includes(needle) ||
        opt.label.toLowerCase().includes(needle)
    );
  }, [query]);

  const selected = ICON_BY_NAME.get(value);

  function selectIcon(name: string): void {
    onChange(name);
    setOpened(false);
  }

  // `#moo-studio-portal` is rendered by MooStudioLayout for exactly this
  // purpose (studioPortal.ts); document.body is the fallback for any future
  // caller of this shared component outside Moo Studio.
  const portalTarget =
    typeof document === 'undefined'
      ? null
      : document.getElementById('moo-studio-portal') ?? document.body;

  const triggerRect = opened
    ? triggerRef.current?.getBoundingClientRect()
    : undefined;

  return (
    <Stack gap={4}>
      <Text size="sm" fw={500}>
        {label}
      </Text>
      <UnstyledButton
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        aria-label="Choose icon"
        aria-expanded={opened}
        aria-haspopup="dialog"
        onClick={() => setOpened((prev) => !prev)}
      >
        <span className={styles.iconPreview}>
          <FontAwesomeIcon icon={selected ? selected.icon : faImage} />
        </span>
        <Text size="sm" className={styles.triggerLabel} truncate>
          {selected ? selected.label : value || 'No icon selected'}
        </Text>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={styles.chevron}
          size="xs"
        />
      </UnstyledButton>
      {opened &&
        portalTarget &&
        triggerRect &&
        createPortal(
          <div
            ref={dropdownRef}
            className={styles.dropdown}
            role="dialog"
            // Runtime-measured position — can't be a static CSS-module rule;
            // same exception AgentStudioPage.tsx's dropdown already takes.
            style={{
              position: 'fixed',
              top: triggerRect.bottom + 4,
              left: triggerRect.left,
            }}
          >
            <TextInput
              placeholder="Search icons..."
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              leftSection={
                <FontAwesomeIcon icon={faMagnifyingGlass} size="xs" />
              }
              size="xs"
            />
            <div className={styles.grid}>
              {filtered.map((opt) => {
                const isSelected = opt.name === value;
                return (
                  <button
                    key={opt.name}
                    type="button"
                    className={
                      isSelected
                        ? `${styles.cell} ${styles.selected}`
                        : styles.cell
                    }
                    aria-label={opt.label}
                    aria-pressed={isSelected}
                    onClick={() => selectIcon(opt.name)}
                  >
                    <FontAwesomeIcon icon={opt.icon} />
                  </button>
                );
              })}
              {filtered.length === 0 && (
                <Text size="sm" c="dimmed" className={styles.noResults}>
                  No icons found
                </Text>
              )}
            </div>
            {/* The grid can only ever ASSIGN an icon, so without this there is
                no way back to "no icon" once one is picked — the free-text
                field this replaced could always be emptied. Rendered only
                when there is something to clear, so it never reads as an
                action on an already-empty field. */}
            {value && (
              <UnstyledButton
                type="button"
                className={styles.clearButton}
                onClick={() => selectIcon('')}
              >
                <Text size="xs" c="dimmed">
                  Clear icon
                </Text>
              </UnstyledButton>
            )}
          </div>,
          portalTarget
        )}
    </Stack>
  );
}
